import { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import {
  buildSuiteCounts,
  type SuiteStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { SuiteExecutionsTable } from '@/features/executions/components/SuiteExecutionsTable';
import { querySuiteExecutionStatusSummary } from '@/features/executions/data';
import { EntityTrendsPageShell } from '@/features/shared/components/EntityTrendsPageShell';
import { useCloneDateWindow } from '@/features/shared/hooks/useCloneDateWindow';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { resolveEntityTags } from '@/features/shared/tags';
import { SuitesTable } from '@/features/suites/components/SuitesTable';
import { SuitesTableClone } from '@/features/suites/components/SuitesTableClone';
import { querySuites, querySuiteSummary } from '@/features/suites/data/suites';
import {
  SuitesCloneToolbar,
  type CloneDateQuickFilter,
} from '@/features/suites/pages/SuitesCloneToolbar';
import {
  cloneDefaultColumnVisibilityModel,
  cloneDefaultSortDirection,
  cloneDefaultSortKey,
  cloneSortFieldMap,
  type CloneSortDirection,
  type CloneSortKey,
} from '@/features/suites/pages/suitesPageCloneConfig';
import { suiteExecutionSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { gridPaths, queryKeys, useResolvedFeatureFlags } from '@/shared/hooks';
import { Stack } from '@/ui';
import { buildInFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type {
  QueryResult,
  SuiteExecutionStatus,
  SuiteExecutionStatusSummaryDto,
  SuiteListItemDto,
  SuiteSummaryDto,
} from '@flowqa/shared';

export const SuitesPage = (): ReactElement => {
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const cloneTableEnabled = featureFlags?.suitesCloneTable ?? false;
  const viewModel = useEntityTrendsPage<
    SuiteListItemDto,
    SuiteSummaryDto | SuiteExecutionStatusSummaryDto
  >({
    loadingMessage: 'Loading suites...',
    metricsScope: 'suite',
    gridPath: gridPaths.suites,
    executionGridPath: gridPaths.suiteExecutions,
    entitySearchField: 'name',
    executionSearchField: 'suiteName',
    statusOptions: suiteExecutionSummaryStatusOptions.map((option) => ({
      value: option.value as Exclude<SuiteStatusKey, 'untested'>,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.suiteExecutionSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.suiteSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.suiteExecutionSummaryPrefix,
    queryLatestSummary: querySuiteSummary,
    queryExecutionSummary: querySuiteExecutionStatusSummary,
    buildCounts: buildSuiteCounts,
    selectThresholds: (thresholds) => thresholds.suites,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
        tableDeltasEnabled,
      },
    ): Promise<QueryResult<SuiteListItemDto>> =>
      querySuites(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          project: true,
          latestExecution: true,
          latestTestResults: { counts: true },
          metrics: true,
          metricDeltas: deltasEnabled && tableDeltasEnabled,
        },
      }),
  });

  const [cloneDateQuickFilter, setCloneDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [cloneFromDate, setCloneFromDate] = useState<Dayjs | null>(null);
  const [cloneToDate, setCloneToDate] = useState<Dayjs | null>(null);
  const [debouncedCloneSearch, setDebouncedCloneSearch] = useState('');
  const [cloneColumnVisibilityModel, setCloneColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(cloneDefaultColumnVisibilityModel);
  const [cloneSortKey, setCloneSortKey] = useState<CloneSortKey>(cloneDefaultSortKey);
  const [cloneSortDirection, setCloneSortDirection] =
    useState<CloneSortDirection>(cloneDefaultSortDirection);
  const [cloneFilterStatuses, setCloneFilterStatuses] = useState<SuiteExecutionStatus[]>([]);
  const [cloneFilterTags, setCloneFilterTags] = useState<string[]>([]);
  const [cloneVisibleRows, setCloneVisibleRows] = useState<SuiteListItemDto[]>([]);

  const cloneAvailableTags = useMemo(() => {
    const tags = new Set<string>();
    cloneVisibleRows.forEach((row) => {
      resolveEntityTags(row).forEach((tag) => {
        tags.add(tag);
      });
    });
    return Array.from(tags.values()).sort((left, right) => left.localeCompare(right));
  }, [cloneVisibleRows]);
  const handleDebouncedCloneSearchChange = useCallback((value: string) => {
    setDebouncedCloneSearch(value);
  }, []);
  const showCloneDeltas = viewModel.deltasEnabled;
  const cloneDateWindow = useCloneDateWindow({
    dateQuickFilter: cloneDateQuickFilter,
    fromDate: cloneFromDate,
    toDate: cloneToDate,
  });
  const cloneQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<SuiteListItemDto>> => {
      const toolbarFilters = mergeFilters(
        buildInFilter('latestExecutionStatus', cloneFilterStatuses),
        buildInFilter('tags', cloneFilterTags),
      );
      const sortOverride: ApiSort = {
        field: cloneSortFieldMap[cloneSortKey],
        direction: cloneSortDirection,
      };
      return querySuites(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          sorts: [sortOverride],
          filters: [...input.filters, ...toolbarFilters],
          metricsStartDay: cloneDateWindow.startDay,
          metricsEndDay: cloneDateWindow.endDay,
          previousMetricsStartDay: cloneDateWindow.previousStartDay,
          previousMetricsEndDay: cloneDateWindow.previousEndDay,
          include: {
            project: true,
            latestExecution: true,
            latestTestResults: { counts: true },
            metrics: true,
            metricDeltas: showCloneDeltas,
          },
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.previousEndDay,
      cloneDateWindow.previousStartDay,
      cloneDateWindow.startDay,
      cloneFilterStatuses,
      cloneFilterTags,
      cloneSortDirection,
      cloneSortKey,
      showCloneDeltas,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No suites found for selected date range'
    : formatRangeWindowNoDataText('No suites found', cloneDateQuickFilter, false);
  const cloneStatusFilterKey = useMemo(
    () => [...cloneFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterStatuses],
  );
  const cloneTagFilterKey = useMemo(
    () => [...cloneFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterTags],
  );

  return (
    <Stack gap="md">
      <EntityTrendsPageShell
        viewModel={viewModel}
        title="Suites"
        addButtonLabel="Add Suite"
        searchAriaLabel="Search suites"
        chartTabsAriaLabel="Suites chart tabs"
        pageTabsAriaLabel="Suites page tabs"
        entityTabLabel="Suites"
        chartEmptyStateTitle="No suite executions found"
        entityTableNoRowsText="No suites found"
        executionTableNoRowsText="No suite executions found"
        persistKey="suites-table"
        executionPersistKey="suite-executions-table"
        renderEntityTable={({
          queryKey,
          queryFn,
          fixedFilters,
          showDeltas,
          persistKey,
          noRowsText,
        }) => (
          <SuitesTable
            persistKey={persistKey}
            queryKey={queryKey}
            queryFn={queryFn}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            showDeltas={showDeltas}
            fixedFilters={fixedFilters}
            noRowsText={noRowsText}
          />
        )}
        renderExecutionsTable={({ fixedFilters, persistKey, noRowsText }) => (
          <SuiteExecutionsTable
            persistKey={persistKey}
            fixedFilters={fixedFilters}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            noRowsText={noRowsText}
          />
        )}
      />
      {cloneTableEnabled ? (
        <Panel padding="md" gap="none" contentGap="md" sx={{ backgroundColor: 'background.paper' }}>
          <SuitesCloneToolbar
            onDebouncedCloneSearchChange={handleDebouncedCloneSearchChange}
            columnVisibilityModel={cloneColumnVisibilityModel}
            onColumnVisibilityModelChange={setCloneColumnVisibilityModel}
            sortKey={cloneSortKey}
            sortDirection={cloneSortDirection}
            onSortChange={(nextSortKey, nextSortDirection) => {
              setCloneSortKey(nextSortKey);
              setCloneSortDirection(nextSortDirection);
            }}
            filterStatuses={cloneFilterStatuses}
            onFilterStatusesChange={setCloneFilterStatuses}
            filterTags={cloneFilterTags}
            onFilterTagsChange={setCloneFilterTags}
            dateQuickFilter={cloneDateQuickFilter}
            onDateQuickFilterChange={(next) => {
              setCloneDateQuickFilter(next);
              setCloneFromDate(null);
              setCloneToDate(null);
            }}
            fromDate={cloneFromDate}
            toDate={cloneToDate}
            onFromDateChange={setCloneFromDate}
            onToDateChange={setCloneToDate}
            availableTags={cloneAvailableTags}
          />
          <SuitesTableClone
            persistKey="suites-table-clone"
            queryKey={[
              'suites-table-clone',
              cloneDateQuickFilter,
              viewModel.selectedOrganisationId ?? null,
              viewModel.selectedProjectId ?? null,
              showCloneDeltas ? 'deltas-on' : 'deltas-off',
              cloneSortKey,
              cloneSortDirection,
              cloneStatusFilterKey,
              cloneTagFilterKey,
              cloneDateWindow.startDay,
              cloneDateWindow.endDay,
            ]}
            queryFn={cloneQueryFn}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            showDeltas={showCloneDeltas}
            noRowsText={cloneNoRowsText}
            showToolbar={false}
            disableColumnSorting
            disableColumnMenu
            search={debouncedCloneSearch}
            onRowsChange={(rows) => {
              setCloneVisibleRows(rows);
            }}
            sparklineRangeDays={cloneDateWindow.sparklineRangeDays}
            metricsStartDay={cloneDateWindow.startDay}
            metricsEndDay={cloneDateWindow.endDay}
            columnVisibilityModel={cloneColumnVisibilityModel}
            onColumnVisibilityModelChange={(nextModel) => {
              setCloneColumnVisibilityModel({
                ...nextModel,
                name: true,
              });
            }}
          />
        </Panel>
      ) : null}
    </Stack>
  );
};
